import { Link } from "react-router-dom";
import { useState } from "react";
import {
  Wrench, Shield, Clock, TrendingDown, QrCode, Calendar, MessageSquare, BarChart3,
  CheckCircle2, ArrowRight, ArrowLeft, Sparkles, Zap, Star, Award, Phone, Download, LogIn
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useLang } from "../context/LanguageContext";
import { EMIRATES } from "../lib/i18n";
import { SubmitBar } from "../components/SubmitBar";
import { MaintenanceReviews } from "../components/MaintenanceReviews";
import { EmergencyLine } from "../components/EmergencyLine";
import { BACKEND_URL } from "../lib/api";

const PLAN_ICONS = [Shield, Star, Award];
const FEATURE_ICONS = [Calendar, QrCode, BarChart3, MessageSquare, TrendingDown, Zap];
const STAT_ICONS = [TrendingDown, Clock, Sparkles];

export default function Maintenance() {
  const { t, isRTL } = useLang();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const m = t.maintenance;

  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    phone: "",
    email: "",
    emirate: "dubai",
    plan: "professional",
    equipment_count: "",
    outlets: "1",
    notes: "",
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const payload = {
    name: form.contact_name || form.company_name,
    phone: form.phone,
    project_type: "restaurant",
    emirate: form.emirate,
    message: `Maintenance Plan Inquiry — Plan: ${form.plan}`,
    company_name: form.company_name,
    email: form.email,
    preferred_contact: "call",
  };
  const extra = {
    plan: form.plan,
    equipment_count: form.equipment_count,
    outlets: form.outlets,
    notes: form.notes,
  };

  return (
    <div data-testid="maintenance-page">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-amber-50 via-white to-red-50/40 overflow-hidden">
        <div className="absolute top-16 end-16 w-80 h-80 rounded-full bg-[#ef4444]/8 blur-3xl pointer-events-none" />
        <div className="absolute bottom-16 start-16 w-80 h-80 rounded-full bg-amber-200/30 blur-3xl pointer-events-none" />

        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-1.5 text-[10px] tracking-[0.3em] text-[#ef4444] font-semibold mb-4 uppercase shadow-sm">
              <Wrench className="w-3.5 h-3.5" />
              {m.eyebrow}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5 text-[#0891b2]">
              {m.title}
            </h1>
            <p className="text-slate-600 text-lg leading-relaxed mb-8">{m.subtitle}</p>

            <div className="flex flex-wrap gap-3 mb-8">
              <a href="#plans">
                <Button data-testid="maint-hero-plans" className="bg-[#ef4444] hover:bg-[#dc2626] text-white h-12 px-6 font-semibold">
                  {m.viewPlans} <Arrow className="w-4 h-4 ms-2" />
                </Button>
              </a>
              <a href="#request">
                <Button variant="outline" data-testid="maint-hero-req" className="border-[#0891b2] text-[#0891b2] hover:bg-[#0891b2] hover:text-white h-12 px-6 font-semibold">
                  {m.requestQuote}
                </Button>
              </a>
              <Link to="/client-portal">
                <Button variant="outline" data-testid="maint-hero-portal" className="border-slate-300 text-slate-700 hover:bg-slate-800 hover:text-white hover:border-slate-800 h-12 px-6 font-semibold">
                  <LogIn className="w-4 h-4 me-2" /> {m.clientPortal}
                </Button>
              </Link>
            </div>
          </div>

          {/* Right — stats card */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              {m.stats.map((s, i) => {
                const Icon = STAT_ICONS[i] || TrendingDown;
                return (
                  <div
                    key={i}
                    data-testid={`maint-stat-${i}`}
                    className={`${i === 2 ? "col-span-2" : ""} bg-white rounded-2xl p-6 shadow-lg border border-slate-100`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-[#ef4444]" />
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
                        {s.label}
                      </div>
                    </div>
                    <div className="font-display text-4xl text-[#0891b2] mb-1 leading-none">
                      {s.value}
                    </div>
                    <div className="text-slate-500 text-xs leading-relaxed">
                      {s.desc}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="inline-block text-[10px] tracking-[0.3em] text-[#ef4444] font-semibold mb-3 uppercase">
              {m.featuresEyebrow}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0891b2] mb-3 leading-tight">
              {m.featuresTitle}
            </h2>
            <p className="text-slate-600">{m.featuresSubtitle}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {m.features.map((f, i) => {
              const Icon = FEATURE_ICONS[i] || Calendar;
              return (
                <div
                  key={i}
                  data-testid={`maint-feature-${i}`}
                  className="group bg-white border border-slate-200 hover:border-[#ef4444] hover:shadow-xl rounded-2xl p-6 transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-red-50 group-hover:bg-[#ef4444] flex items-center justify-center mb-5 transition-colors">
                    <Icon className="w-5 h-5 text-[#ef4444] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-display text-lg text-[#0891b2] mb-2 leading-snug">{f.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">{f.text}</p>
                  <ul className="space-y-1.5">
                    {f.bullets.map((b, j) => (
                      <li key={j} className="flex items-start gap-2 text-xs text-slate-600">
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0 mt-0.5" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section id="plans" className="py-20 bg-slate-50">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="inline-block text-[10px] tracking-[0.3em] text-[#ef4444] font-semibold mb-3 uppercase">
              {m.plansEyebrow}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0891b2] mb-3 leading-tight">
              {m.plansTitle}
            </h2>
            <p className="text-slate-600">{m.plansSubtitle}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {m.plans.map((p, i) => {
              const Icon = PLAN_ICONS[i] || Shield;
              const featured = p.featured;
              return (
                <div
                  key={i}
                  data-testid={`maint-plan-${i}`}
                  className={`relative bg-white rounded-2xl overflow-hidden transition-all hover:-translate-y-1 ${
                    featured
                      ? "border-2 border-[#ef4444] shadow-2xl"
                      : "border border-slate-200 shadow-sm hover:shadow-lg"
                  }`}
                >
                  {featured && (
                    <div className="absolute top-0 start-0 end-0 bg-[#ef4444] text-white text-center py-1.5 text-[10px] tracking-widest uppercase font-semibold">
                      {m.mostPopular}
                    </div>
                  )}
                  <div className={`p-8 ${featured ? "pt-12" : ""}`}>
                    <div className={`w-14 h-14 rounded-2xl mb-5 flex items-center justify-center ${
                      featured ? "bg-[#ef4444] text-white" : "bg-cyan-50 text-[#0891b2]"
                    }`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">{p.eyebrow}</div>
                    <h3 className="font-display text-2xl text-[#0891b2] mb-3">{p.name}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-6">{p.tagline}</p>

                    <div className="mb-6 pb-6 border-b border-slate-100">
                      <div className="flex items-baseline gap-2">
                        <span className="font-display text-4xl text-[#0891b2]">{p.priceFrom}</span>
                        <span className="text-slate-500 text-sm">{p.priceUnit}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{p.priceNote}</div>
                    </div>

                    <ul className="space-y-2.5 mb-8">
                      {p.features.map((f, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-slate-700">
                          <CheckCircle2 className={`w-4 h-4 shrink-0 mt-0.5 ${featured ? "text-[#ef4444]" : "text-green-600"}`} />
                          {f}
                        </li>
                      ))}
                    </ul>

                    <a href="#request" onClick={() => set("plan", p.key)}>
                      <Button
                        data-testid={`maint-plan-cta-${i}`}
                        className={`w-full h-12 font-semibold ${
                          featured
                            ? "bg-[#ef4444] hover:bg-[#dc2626] text-white"
                            : "bg-white border-2 border-[#0891b2] text-[#0891b2] hover:bg-[#0891b2] hover:text-white"
                        }`}
                      >
                        {m.selectPlan}
                      </Button>
                    </a>
                    <a
                      href={`${BACKEND_URL}/api/maintenance/contract-pdf?plan=${p.key}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`maint-plan-pdf-${i}`}
                      className="mt-2 inline-flex items-center justify-center gap-1.5 w-full text-xs text-slate-500 hover:text-[#ef4444] font-medium py-2 transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" /> {m.downloadContract}
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="py-14 bg-white border-y border-slate-100">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {m.trust.map((t, i) => (
              <div key={i} data-testid={`maint-trust-${i}`} className="text-center">
                <div className="font-display text-3xl text-[#ef4444] mb-1">{t.value}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Reviews */}
      <MaintenanceReviews />

      {/* Emergency Line */}
      <EmergencyLine />

      {/* Request form */}
      <section id="request" className="py-20 bg-gradient-to-br from-cyan-50 via-white to-red-50/30">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-lg overflow-hidden">
            <div className="bg-[#0891b2] text-white p-6">
              <div className="inline-block text-[10px] tracking-[0.3em] text-red-300 font-semibold mb-2 uppercase">
                {m.formEyebrow}
              </div>
              <h2 className="font-display text-2xl">{m.formTitle}</h2>
              <p className="text-white/70 text-sm mt-1">{m.formSubtitle}</p>
            </div>
            <div className="p-6 sm:p-8 space-y-5">
              <Field label={m.selectedPlan}>
                <div className="grid grid-cols-3 gap-2">
                  {m.plans.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => set("plan", p.key)}
                      data-testid={`maint-fplan-${p.key}`}
                      className={`text-xs py-2.5 rounded-lg font-semibold transition-all border ${
                        form.plan === p.key
                          ? "bg-[#ef4444] text-white border-[#ef4444]"
                          : "bg-white text-slate-600 border-slate-200 hover:border-[#ef4444]"
                      }`}
                    >
                      {p.name}
                    </button>
                  ))}
                </div>
              </Field>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={m.company} required>
                  <Input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="maint-company" className="h-11" />
                </Field>
                <Field label={m.contactName} required>
                  <Input required value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="maint-cname" className="h-11" />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={m.phone} required>
                  <Input required dir="ltr" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="maint-phone" className="h-11" placeholder="+971 4-288-2777" />
                </Field>
                <Field label={m.email}>
                  <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="maint-email" className="h-11" />
                </Field>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label={m.equipmentCount}>
                  <Input dir="ltr" value={form.equipment_count} onChange={(e) => set("equipment_count", e.target.value)} data-testid="maint-eqcount" className="h-11" placeholder="25" />
                </Field>
                <Field label={m.outlets}>
                  <Input dir="ltr" value={form.outlets} onChange={(e) => set("outlets", e.target.value)} data-testid="maint-outlets" className="h-11" placeholder="1" />
                </Field>
                <Field label={m.emirate}>
                  <Select value={form.emirate} onValueChange={(v) => set("emirate", v)}>
                    <SelectTrigger data-testid="maint-em" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {EMIRATES.map((e) => <SelectItem key={e} value={e}>{t.emirates[e]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label={m.notes}>
                <Textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="maint-notes" placeholder={m.notesHint} />
              </Field>

              <div className="pt-4 border-t border-slate-100">
                <SubmitBar payload={payload} source="maintenance" submitLabel={m.submit} extraData={extra} />
                <p className="text-xs text-slate-500 mt-3">{m.formFooter}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-700">
        {label} {required && <span className="text-[#ef4444]">*</span>}
      </Label>
      {children}
    </div>
  );
}
